import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyForm = {
  name: '',
  slug: '',
  sort_order: 0,
};

function makeSlug(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function AdminSiteRealisationsCategoriesPage() {
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [slugTouched, setSlugTouched] = React.useState(false);

  const fetchAll = async () => {
    setError('');
    try {
      const res = await pb.collection('site_realisations_categories').getList(1, 200, { sort: 'sort_order, name' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des catégories');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchAll();
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => clearTimeout(t);
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_realisations_categories').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...items];
    await persistOrder(ordered);
    await fetchAll();
  };

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    setForm({ ...emptyForm, sort_order: items.length ? Math.max(...items.map(i => Number(i.sort_order || 0))) + 1 : 0 });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setSlugTouched(true);
    setForm({
      name: it?.name || '',
      slug: it?.slug || '',
      sort_order: Number(it?.sort_order || 0),
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
  };

  const handleNameChange = (value) => {
    setForm((f) => {
      const next = { ...f, name: value };
      if (!slugTouched) next.slug = makeSlug(value);
      return next;
    });
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name || '',
        slug: form.slug || '',
        sort_order: Number(form.sort_order || 0),
      };
      if (editing?.id) await pb.collection('site_realisations_categories').update(editing.id, payload);
      else await pb.collection('site_realisations_categories').create(payload);
      await fetchAll();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await pb.collection('site_realisations_categories').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const move = async (id, dir) => {
    const ordered = [...items].sort((a, b) => {
      const ao = Number(a.sort_order || 0);
      const bo = Number(b.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return (a.created || '').localeCompare(b.created || '');
    });
    const idx = ordered.findIndex(i => i.id === id);
    if (idx < 0) return;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;
    const a = ordered[idx];
    const b = ordered[swapWith];
    const ao = Number(a.sort_order || 0);
    const bo = Number(b.sort_order || 0);
    try {
      await Promise.all([
        pb.collection('site_realisations_categories').update(a.id, { sort_order: bo }),
        pb.collection('site_realisations_categories').update(b.id, { sort_order: ao }),
      ]);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Réalisations · Catégories</h1>
          <p style={{ marginTop: 6 }}>Gérez les catégories associées aux réalisations.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Nouvelle catégorie</span>
          </button>
        </div>
      </div>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"]{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="grid">
          {items.map((it, idx) => (
              <div
                key={it.id}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => {
                  e.preventDefault();
                  onDrop();
                }}
                title={`Glissez pour réordonner (position ${idx + 1})`}
                style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))', cursor: 'grab' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 999, background: '#2d3556', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700 }}>
                    {(it.name || '—').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name || '—'}</div>
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>slug: {it.slug || '—'} · ordre: {idx + 1}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <button className="header-pill" onClick={() => move(it.id, 'up')} title="Monter">⬆</button>
                  <button className="header-pill" onClick={() => move(it.id, 'down')} title="Descendre">⬇</button>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button className="header-pill" title="Modifier" onClick={() => openEdit(it)}>✏️</button>
                    <button className="header-pill" title="Supprimer" onClick={() => handleDelete(it.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}

          {items.length === 0 && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <h3 style={{ margin: 0 }}>Aucune catégorie</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>Commencez par ajouter votre première catégorie.</p>
                <button className="btn-primary" onClick={openCreate} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                  border: '1px solid rgba(79,70,229,0.6)',
                  boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                  color: 'white'
                }}>
                  <span>Nouvelle catégorie</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={(e) => handleNameChange(e.target.value)} />
              </div>
              <div className="field">
                <label>Slug</label>
                <input type="text" value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="ex: web, seo..." />
                <small>Doit être unique</small>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteRealisationsCategoriesPage;
